import { useState, useEffect, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO, isSameDay, addMonths, startOfMonth, eachDayOfInterval, startOfDay } from "date-fns";
import { de } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface DynamicSearchCalendarProps {
  listingId?: string;
  roomTypeId?: string;
  onDateSelect?: (checkin: Date | null, checkout: Date | null) => void;
  selectedCheckin?: Date | null;
  selectedCheckout?: Date | null;
  mode?: "search" | "booking";
}

interface BookingData {
  check_in_date: string;
  check_out_date: string;
  status: string;
  room_type_id?: string;
}

export const DynamicSearchCalendar = ({
  listingId,
  roomTypeId,
  onDateSelect,
  selectedCheckin,
  selectedCheckout,
  mode = "search"
}: DynamicSearchCalendarProps) => {
  const isMobile = useIsMobile();
  const [bookings, setBookings] = useState<BookingData[]>([]);
  const [currentMonth, setCurrentMonth] = useState(() => {
    // Start with the month of selectedCheckin if available, otherwise current month
    return selectedCheckin ? startOfMonth(selectedCheckin) : startOfMonth(new Date());
  });
  const [roomCapacity, setRoomCapacity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [selectingCheckout, setSelectingCheckout] = useState(false);

  // Fetch bookings for availability checking
  useEffect(() => {
    if (listingId) {
      fetchBookings();
      if (roomTypeId) {
        fetchRoomCapacity();
      }
    }
  }, [listingId, roomTypeId]);

  const fetchBookings = async () => {
    if (!listingId) return;
    
    setLoading(true);
    try {
      // Use the secure RPC function that doesn't expose guest data
      const { data, error } = await supabase.rpc('get_booking_blocks', {
        p_listing_id: listingId,
        p_room_type_id: roomTypeId || null
      });

      if (error) throw error;
      setBookings(data || []);
    } catch (error) {
      console.error('Error fetching booking blocks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomCapacity = async () => {
    if (!roomTypeId) return;
    
    try {
      const { data, error } = await supabase
        .from('listing_room_types')
        .select('available_quantity')
        .eq('id', roomTypeId)
        .single();

      if (error) throw error;
      setRoomCapacity(data?.available_quantity || 1);
    } catch (error) {
      console.error('Error fetching room capacity:', error);
    }
  };

  // Calculate date availability
  const dateAvailability = useMemo(() => {
    const availability = new Map<string, { booked: number; total: number }>();
    
    bookings.forEach(booking => {
      const checkIn = parseISO(booking.check_in_date);
      const checkOut = parseISO(booking.check_out_date);
      
      for (let date = new Date(checkIn); date < checkOut; date.setDate(date.getDate() + 1)) {
        const dateKey = format(date, 'yyyy-MM-dd');
        const current = availability.get(dateKey) || { booked: 0, total: roomCapacity };
        availability.set(dateKey, { 
          booked: current.booked + 1, 
          total: roomCapacity 
        });
      }
    });
    
    return availability;
  }, [bookings, roomCapacity]);

  // Fully booked dates
  const fullyBookedDates = useMemo(() => 
    Array.from(dateAvailability.entries())
      .filter(([_, availability]) => availability.booked >= availability.total)
      .map(([dateKey]) => parseISO(dateKey)),
    [dateAvailability]
  );

  // Partially booked dates
  const partiallyBookedDates = useMemo(() =>
    Array.from(dateAvailability.entries())
      .filter(([_, availability]) => availability.booked > 0 && availability.booked < availability.total)
      .map(([dateKey]) => parseISO(dateKey)),
    [dateAvailability]
  );

  const handleDateClick = (date: Date | undefined) => {
    if (!date) return;

    // Check if date is fully booked
    const dateKey = format(date, 'yyyy-MM-dd');
    const avail = dateAvailability.get(dateKey);
    if (avail && avail.booked >= avail.total) {
      return; // Don't allow selection of fully booked dates
    }

    if (!selectedCheckin || selectingCheckout) {
      // Selecting checkout date
      if (selectedCheckin && date > selectedCheckin) {
        onDateSelect?.(selectedCheckin, date);
        setSelectingCheckout(false);
      } else {
        // Start new selection
        onDateSelect?.(date, null);
        setSelectingCheckout(true);
      }
    } else {
      // Selecting checkin date
      onDateSelect?.(date, null);
      setSelectingCheckout(true);
    }
  };

  const isDateDisabled = (date: Date) => {
    // Get today's date at start of day in local timezone
    const today = startOfDay(new Date());
    // Normalize the input date to start of day as well
    const normalizedDate = startOfDay(date);
    
    // Past dates are disabled (dates before today)
    if (normalizedDate < today) {
      return true;
    }

    // If selecting checkout, disable dates before checkin
    if (selectingCheckout && selectedCheckin && normalizedDate <= startOfDay(selectedCheckin)) {
      return true;
    }

    // Check if date is fully booked
    const dateKey = format(normalizedDate, 'yyyy-MM-dd');
    const avail = dateAvailability.get(dateKey);
    return avail ? avail.booked >= avail.total : false;
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => addMonths(prev, -1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  return (
    <Card className="p-4">
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePreviousMonth}
          disabled={isSameDay(startOfMonth(currentMonth), startOfMonth(new Date()))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        <div className="text-sm font-medium flex items-center gap-2">
          <CalendarIcon className="h-4 w-4" />
          {format(currentMonth, 'MMMM yyyy', { locale: de })}
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handleNextMonth}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className={cn(
        "grid gap-4",
        isMobile ? "grid-cols-1" : "grid-cols-2"
      )}>
        {/* First Month */}
        <Calendar
          mode="single"
          selected={selectedCheckin || undefined}
          onSelect={handleDateClick}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          locale={de}
          disabled={isDateDisabled}
          modifiers={{
            fullyBooked: fullyBookedDates,
            partiallyBooked: partiallyBookedDates,
            checkin: selectedCheckin ? [selectedCheckin] : [],
            checkout: selectedCheckout ? [selectedCheckout] : [],
            inRange: selectedCheckin && selectedCheckout ? 
              eachDayOfInterval({ start: selectedCheckin, end: selectedCheckout }).slice(1, -1) : []
          }}
          modifiersClassNames={{
            fullyBooked: "bg-destructive/20 text-destructive line-through cursor-not-allowed hover:bg-destructive/20",
            partiallyBooked: "bg-orange-100 dark:bg-orange-900/30 text-orange-900 dark:text-orange-100 border border-orange-400",
            checkin: "bg-primary text-primary-foreground hover:bg-primary/90 font-bold",
            checkout: "bg-primary text-primary-foreground hover:bg-primary/90 font-bold",
            inRange: "bg-primary/20 hover:bg-primary/30"
          }}
          className="pointer-events-auto"
          classNames={{
            nav: "hidden"
          }}
        />

        {/* Second Month (Desktop only) */}
        {!isMobile && (
          <Calendar
            mode="single"
            selected={selectedCheckout || undefined}
            onSelect={handleDateClick}
            month={addMonths(currentMonth, 1)}
            locale={de}
            disabled={isDateDisabled}
            modifiers={{
              fullyBooked: fullyBookedDates,
              partiallyBooked: partiallyBookedDates,
              checkin: selectedCheckin ? [selectedCheckin] : [],
              checkout: selectedCheckout ? [selectedCheckout] : [],
              inRange: selectedCheckin && selectedCheckout ? 
                eachDayOfInterval({ start: selectedCheckin, end: selectedCheckout }).slice(1, -1) : []
            }}
            modifiersClassNames={{
              fullyBooked: "bg-destructive/20 text-destructive line-through cursor-not-allowed hover:bg-destructive/20",
              partiallyBooked: "bg-orange-100 dark:bg-orange-900/30 text-orange-900 dark:text-orange-100 border border-orange-400",
              checkin: "bg-primary text-primary-foreground hover:bg-primary/90 font-bold",
              checkout: "bg-primary text-primary-foreground hover:bg-primary/90 font-bold",
              inRange: "bg-primary/20 hover:bg-primary/30"
            }}
            className="pointer-events-auto"
            classNames={{
              nav: "hidden"
            }}
          />
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t">
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-primary rounded" />
            <span className="text-muted-foreground">Gewählt</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-primary/20 rounded" />
            <span className="text-muted-foreground">Zeitraum</span>
          </div>
          {roomCapacity > 1 && (
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-orange-100 dark:bg-orange-900/30 border border-orange-400 rounded" />
              <span className="text-muted-foreground">Teilweise belegt</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-destructive/20 rounded" />
            <span className="text-muted-foreground">Ausgebucht</span>
          </div>
        </div>

        {/* Room availability info for partially booked dates */}
        {roomCapacity > 1 && partiallyBookedDates.length > 0 && (
          <div className="mt-3 p-2 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded text-xs">
            <div className="font-medium text-orange-800 dark:text-orange-200 mb-1">
              Verfügbare Zimmer an teilweise belegten Tagen:
            </div>
            <div className="flex flex-wrap gap-2">
              {Array.from(dateAvailability.entries())
                .filter(([_, avail]) => avail.booked > 0 && avail.booked < avail.total)
                .slice(0, 5)
                .map(([dateKey, avail]) => (
                  <Badge
                    key={dateKey}
                    variant="outline"
                    className="bg-orange-100 dark:bg-orange-900/50 border-orange-300 dark:border-orange-700 text-orange-800 dark:text-orange-200"
                  >
                    {format(parseISO(dateKey), 'dd.MM', { locale: de })}: {avail.total - avail.booked}/{avail.total} frei
                  </Badge>
                ))}
              {Array.from(dateAvailability.entries())
                .filter(([_, avail]) => avail.booked > 0 && avail.booked < avail.total).length > 5 && (
                <Badge variant="secondary" className="text-muted-foreground">
                  +{Array.from(dateAvailability.entries())
                    .filter(([_, avail]) => avail.booked > 0 && avail.booked < avail.total).length - 5} weitere
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Selection Info */}
        {selectedCheckin && (
          <div className="mt-3 p-2 bg-muted/30 rounded text-sm">
            <div className="font-medium mb-1">
              {selectingCheckout ? "Wähle Abreisedatum" : "Anreise gewählt"}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Anreise: {format(selectedCheckin, 'dd.MM.yyyy', { locale: de })}</span>
              {selectedCheckout && (
                <>
                  <span>→</span>
                  <span>Abreise: {format(selectedCheckout, 'dd.MM.yyyy', { locale: de })}</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="absolute inset-0 bg-background/50 flex items-center justify-center rounded-lg">
          <div className="text-sm text-muted-foreground">Lade Verfügbarkeit...</div>
        </div>
      )}
    </Card>
  );
};